// Проекция Гаусса-Крюгера на эллипсоиде Красовского: основа километровой
// сетки советских топографических карт. Зоны шестиградусные, номер зоны
// ведёт отсчёт от 0°, осевой меридиан - 6n-3, ложный восток - 500 км плюс
// номер зоны в миллионах (так подписана сетка на листах).

const A = 6378245;
const F = 1 / 298.3;
const N = F / (2 - F);
const RECT_RADIUS = (A / (1 + N)) * (1 + N ** 2 / 4 + N ** 4 / 64);
const ALPHA = [N / 2 - (2 * N ** 2) / 3 + (5 * N ** 3) / 16, (13 * N ** 2) / 48 - (3 * N ** 3) / 5, (61 * N ** 3) / 240];
const BETA = [N / 2 - (2 * N ** 2) / 3 + (37 * N ** 3) / 96, N ** 2 / 48 + N ** 3 / 15, (17 * N ** 3) / 480];
const DELTA = [2 * N - (2 * N ** 2) / 3 - 2 * N ** 3, (7 * N ** 2) / 3 - (8 * N ** 3) / 5, (56 * N ** 3) / 15];
const RAD = Math.PI / 180;

export const gkZone = (lon: number): number => Math.floor(lon / 6) + 1;
const centralMeridian = (zone: number): number => 6 * zone - 3;

/** lon/lat (градусы) -> { x: север, м; y: восток с ложным смещением, м } в заданной зоне. */
export const toGaussKruger = (lon: number, lat: number, zone = gkZone(lon)): { x: number; y: number } => {
  const phi = lat * RAD;
  const dl = (lon - centralMeridian(zone)) * RAD;
  const e = (2 * Math.sqrt(N)) / (1 + N);
  const t = Math.sinh(Math.atanh(Math.sin(phi)) - e * Math.atanh(e * Math.sin(phi)));
  const xi0 = Math.atan2(t, Math.cos(dl));
  const eta0 = Math.atanh(Math.sin(dl) / Math.sqrt(1 + t * t));
  let xi = xi0;
  let eta = eta0;
  ALPHA.forEach((a, i) => {
    const j = 2 * (i + 1);
    xi += a * Math.sin(j * xi0) * Math.cosh(j * eta0);
    eta += a * Math.cos(j * xi0) * Math.sinh(j * eta0);
  });
  return { x: RECT_RADIUS * xi, y: RECT_RADIUS * eta + 500000 + zone * 1e6 };
};

/** Обратное преобразование: x - север, y - восток с номером зоны. */
export const fromGaussKruger = (x: number, y: number, zone = Math.floor(y / 1e6)): [number, number] => {
  const xi = x / RECT_RADIUS;
  const eta = (y - zone * 1e6 - 500000) / RECT_RADIUS;
  let xi0 = xi;
  let eta0 = eta;
  BETA.forEach((b, i) => {
    const j = 2 * (i + 1);
    xi0 -= b * Math.sin(j * xi) * Math.cosh(j * eta);
    eta0 -= b * Math.cos(j * xi) * Math.sinh(j * eta);
  });
  const chi = Math.asin(Math.sin(xi0) / Math.cosh(eta0));
  let phi = chi;
  DELTA.forEach((d, i) => {
    phi += d * Math.sin(2 * (i + 1) * chi);
  });
  const lon = centralMeridian(zone) + Math.atan2(Math.sinh(eta0), Math.cos(xi0)) / RAD;
  return [lon, phi / RAD];
};

export interface GridBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Шаг сетки в километрах для зума: 1 км на крупном плане, реже на мелком. */
export const gridStepKm = (zoom: number): number | null => {
  if (zoom >= 13) return 1;
  if (zoom >= 12) return 2;
  if (zoom >= 11) return 5;
  if (zoom >= 10) return 10;
  return null;
};

const SAMPLES = 8;
const MAX_LINES = 120;

/**
 * Линии километровой сетки, попадающие в окно. Подпись - две последние цифры
 * километров (как внутри листа на бумажной карте).
 */
export const buildKmGrid = (bounds: GridBounds, zoom: number): GeoJSON.FeatureCollection<GeoJSON.LineString> => {
  const stepKm = gridStepKm(zoom);
  const empty: GeoJSON.FeatureCollection<GeoJSON.LineString> = { type: "FeatureCollection", features: [] };
  if (!stepKm) return empty;
  const zone = gkZone((bounds.west + bounds.east) / 2);
  const corners = [
    [bounds.west, bounds.south], [bounds.east, bounds.south], [bounds.west, bounds.north], [bounds.east, bounds.north],
    [(bounds.west + bounds.east) / 2, bounds.south], [(bounds.west + bounds.east) / 2, bounds.north],
  ].map(([lon, lat]) => toGaussKruger(lon, lat, zone));
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const step = stepKm * 1000;
  const [xMin, xMax] = [Math.min(...xs) - step, Math.max(...xs) + step];
  const [yMin, yMax] = [Math.min(...ys) - step, Math.max(...ys) + step];
  const x0 = Math.floor(xMin / step) * step;
  const y0 = Math.floor(yMin / step) * step;
  if ((xMax - x0) / step + (yMax - y0) / step > MAX_LINES) return empty;

  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  const line = (pts: [number, number][], km: number) =>
    features.push({
      type: "Feature",
      properties: { label: String(Math.floor(km) % 100).padStart(2, "0") },
      geometry: { type: "LineString", coordinates: pts.map(([x, y]) => fromGaussKruger(x, y, zone)) },
    });
  for (let y = y0; y <= yMax; y += step) {
    line(Array.from({ length: SAMPLES + 1 }, (_, i) => [xMin + ((xMax - xMin) * i) / SAMPLES, y] as [number, number]), (y - zone * 1e6) / 1000);
  }
  for (let x = x0; x <= xMax; x += step) {
    line(Array.from({ length: SAMPLES + 1 }, (_, i) => [x, yMin + ((yMax - yMin) * i) / SAMPLES] as [number, number]), x / 1000);
  }
  return { type: "FeatureCollection", features };
};
